import { FilterOperators } from '@kirin/api-client/contracts/table';
import type { FilterValue } from '@kirin/api-client/contracts/table';
import {
  USER_ALERTS_FILTERABLE_FIELDS,
  ALERT_OCCURRENCES_FILTERABLE_FIELDS,
  type UserAlertsFilterableField,
} from '@kirin/api-client/contracts/alerts';
import { USER_ALERTS_COLUMN_URL_KEYS } from './constants';

/**
 * User alerts table config for URL state parsing.
 * Table ID must match the id prop on the DataTable component in index.tsx.
 */
const USER_ALERTS_TABLE_CONFIG = {
  tableId: 'user-alt',
} as const;

/**
 * Canonical URL key mapping for user alerts filterable fields.
 * Maps USER_ALERTS_FILTERABLE_FIELDS values to their URL persistence keys.
 */
const USER_ALERTS_FILTER_URL_KEYS: Record<string, string> = {
  [USER_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT]:
    USER_ALERTS_COLUMN_URL_KEYS.lastSeenAt,
  [USER_ALERTS_FILTERABLE_FIELDS.FILE_PATH]:
    USER_ALERTS_COLUMN_URL_KEYS.filePath,
  [USER_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES]:
    USER_ALERTS_COLUMN_URL_KEYS.clientTypes,
  [USER_ALERTS_FILTERABLE_FIELDS.SCORE]: USER_ALERTS_COLUMN_URL_KEYS.score,
  [USER_ALERTS_FILTERABLE_FIELDS.SEVERITY]:
    USER_ALERTS_COLUMN_URL_KEYS.severity,
  [USER_ALERTS_FILTERABLE_FIELDS.OUTCOME]: USER_ALERTS_COLUMN_URL_KEYS.outcome,
  [USER_ALERTS_FILTERABLE_FIELDS.TYPE]: USER_ALERTS_COLUMN_URL_KEYS.type,
  [USER_ALERTS_FILTERABLE_FIELDS.OCCURRENCE_COUNT]:
    USER_ALERTS_COLUMN_URL_KEYS.occurrenceCount,
  [USER_ALERTS_FILTERABLE_FIELDS.FIRST_SEEN_AT]:
    USER_ALERTS_COLUMN_URL_KEYS.firstSeenAt,
};

/**
 * Mapping from user alerts table filterable fields to their equivalent
 * occurrences filterable fields. Only fields applicable to individual
 * occurrence rows are included.
 *
 * Note: users is excluded — user alerts are already single-user scoped.
 *
 * Both LAST_SEEN_AT and FIRST_SEEN_AT map to occurrences `lastSeenAt`
 * (per-row `scannedAt`) — each occurrence has a single timestamp.
 * When both are active, we deduplicate below keeping only the
 * lastSeenAt-sourced filter (direct semantic match).
 */
const USER_MAIN_TO_OCCURRENCES_FIELD_MAP: Partial<
  Record<UserAlertsFilterableField, string>
> = {
  [USER_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.LAST_SEEN_AT,
  [USER_ALERTS_FILTERABLE_FIELDS.FIRST_SEEN_AT]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.LAST_SEEN_AT,
  [USER_ALERTS_FILTERABLE_FIELDS.FILE_PATH]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.FILE_PATH,
  [USER_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.CLIENT_TYPES,
  [USER_ALERTS_FILTERABLE_FIELDS.SCORE]:
    ALERT_OCCURRENCES_FILTERABLE_FIELDS.SCORE,
};

/**
 * Extract filters applicable to the occurrences modal from the current URL
 * search params for the user/singleton alerts table. Maps only applicable
 * fields (lastSeenAt, filePath, clientTypes, score) to the occurrences API
 * filter shape.
 *
 * Filters that have no equivalent in the occurrences table (severity, outcome,
 * type, occurrenceCount, firstSeenAt) are silently ignored.
 */
export function getUserOccurrencesFiltersFromSearchParams(
  searchParams: URLSearchParams
): FilterValue[] {
  const { tableId } = USER_ALERTS_TABLE_CONFIG;
  const filters: { filter: FilterValue; sourceField: string }[] = [];

  // Build reverse map: urlKey → field name
  const urlKeyToField = Object.fromEntries(
    Object.entries(USER_ALERTS_FILTER_URL_KEYS).map(([field, urlKey]) => [
      urlKey,
      field,
    ])
  );

  // URL pattern: {tableId}.{urlKey}[{operator}]={csv values}
  const prefix = `${tableId}.`;

  searchParams.forEach((value, key) => {
    if (!key.startsWith(prefix)) return;

    const rest = key.slice(prefix.length);
    const bracketMatch = rest.match(/^([^[]+)\[([^\]]+)\]$/);
    if (!bracketMatch) return;

    const urlKey = bracketMatch[1];
    const operator = bracketMatch[2];
    const mainField = urlKeyToField[urlKey];
    if (!mainField) return;

    // Check if this field maps to an occurrences-applicable field
    const occField =
      USER_MAIN_TO_OCCURRENCES_FIELD_MAP[
        mainField as UserAlertsFilterableField
      ];
    if (!occField) return;

    const decoded = value.split(',').map(v => {
      try {
        return decodeURIComponent(v.trim());
      } catch {
        return v.trim();
      }
    });

    // Preserve positional empties for number range filters (e.g. score
    // ",80" means max=80 with no min). Mirrors decodeFilterValues logic.
    const isNumberRange =
      mainField === USER_ALERTS_FILTERABLE_FIELDS.SCORE &&
      (operator === FilterOperators.INCLUDES ||
        operator === FilterOperators.EXCLUDES);
    const values = isNumberRange ? decoded : decoded.filter(Boolean);

    if (values.length === 0) return;

    filters.push({
      filter: {
        field: occField,
        operator: operator as FilterValue['operator'],
        value: values,
      },
      sourceField: mainField,
    });
  });

  // Deduplicate: if both firstSeenAt and lastSeenAt mapped to the same
  // occurrences field (lastSeenAt), prefer the lastSeenAt-sourced entry
  // since it's the direct semantic match to per-row scannedAt.
  const byField = new Map<string, (typeof filters)[number]>();
  for (const entry of filters) {
    const existing = byField.get(entry.filter.field);
    if (!existing) {
      byField.set(entry.filter.field, entry);
    } else if (
      entry.sourceField === USER_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT
    ) {
      byField.set(entry.filter.field, entry);
    }
  }

  return [...byField.values()].map(e => e.filter);
}
